
export default function PricingComparison() {
  const features = [
    {
      category: 'Content & Publishing',
      items: [
        { name: 'generated posts per month', starter: '50', professional: '200', business: '1,000', enterprise: 'Unlimited' },
        { name: 'Social media accounts', starter: '3', professional: '10', business: '25', enterprise: 'Unlimited' },
        { name: 'Post scheduling', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Multi-platform publishing', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Bulk scheduling', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Content calendar', starter: true, professional: true, business: true, enterprise: true },
      ],
    },
    {
      category: 'AI Features',
      items: [
        { name: 'AI content generation', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Platform-specific optimization', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Hashtag suggestions', starter: true, professional: true, business: true, enterprise: true },
        { name: 'AI trend analysis', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Competitor insights', starter: false, professional: false, business: true, enterprise: true },
        { name: 'Custom AI training', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
    {
      category: 'Analytics & Reporting',
      items: [
        { name: 'Basic analytics', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Advanced reporting', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Custom reports', starter: false, professional: false, business: true, enterprise: true },
        { name: 'API access', starter: false, professional: false, business: true, enterprise: true },
        { name: 'White-label reporting', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
    {
      category: 'Team & Collaboration',
      items: [
        { name: 'Team members', starter: '1', professional: '5', business: '15', enterprise: 'Unlimited' },
        { name: 'Approval workflows', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Role-based permissions', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Kanban boards', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Custom workflows', starter: false, professional: false, business: true, enterprise: true },
      ],
    },
    {
      category: 'Support',
      items: [
        { name: 'Email support', starter: '48h', professional: '24h', business: '12h', enterprise: '2h' },
        { name: 'Priority support', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Phone support', starter: false, professional: false, business: true, enterprise: true },
        { name: 'Dedicated account manager', starter: false, professional: false, business: false, enterprise: true },
        { name: 'Custom onboarding', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
  ];

  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <i className="ri-check-line text-[#66b3ff] text-2xl"></i>
      ) : (
        <i className="ri-close-line text-gray-300 text-2xl"></i>
      );
    }
    return <span className="font-semibold text-[#22577A]">{value}</span>;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-table-line mr-2"></i>
            Compare Plans
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-6">
            Detailed Feature Comparison
          </h2>
          <p className="text-xl text-gray-700">
            See exactly what's included in each plan
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white">
                  <th className="px-3 sm:px-6 py-4 text-left font-semibold text-xs sm:text-sm">Features</th>
                  <th className="px-2 sm:px-6 py-4 text-center font-semibold text-xs sm:text-sm">Starter</th>
                  <th className="px-2 sm:px-6 py-4 text-center font-semibold text-xs sm:text-sm">Pro</th>
                  <th className="px-2 sm:px-6 py-4 text-center font-semibold text-xs sm:text-sm">
                    <div className="flex items-center justify-center gap-1">
                      Business
                      <i className="ri-star-fill text-[#b3d9ff]"></i>
                    </div>
                  </th>
                  <th className="px-2 sm:px-6 py-4 text-center font-semibold text-xs sm:text-sm">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {features.map((category, categoryIndex) => (
                  <>
                    <tr
                      key={`category-${categoryIndex}`}
                      className="bg-[#e4f4ff]/20"
                    >
                      <td
                        colSpan={5}
                        className="px-3 sm:px-6 py-3 font-bold text-[#22577A] text-xs sm:text-sm uppercase tracking-wide"
                      >
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr
                        key={`item-${categoryIndex}-${itemIndex}`}
                        className="border-b border-gray-100 hover:bg-[#e4f4ff]/5"
                      >
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-700 text-xs sm:text-sm">{item.name}</td>
                        <td className="px-2 sm:px-6 py-3 sm:py-4 text-center">
                          {renderCell(item.starter)}
                        </td>
                        <td className="px-2 sm:px-6 py-3 sm:py-4 text-center">
                          {renderCell(item.professional)}
                        </td>
                        <td className="px-2 sm:px-6 py-3 sm:py-4 text-center bg-[#e4f4ff]/10">
                          {renderCell(item.business)}
                        </td>
                        <td className="px-2 sm:px-6 py-3 sm:py-4 text-center">
                          {renderCell(item.enterprise)}
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Need help choosing the right plan for your team?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
          >
            Schedule a Demo
            <i className="ri-calendar-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
